import { EllipsedText, FlexColumn, LabeledData, useMoment } from '@gorila-shared-ui/components';
import { StyledLink } from 'baseui/link';
import { Modal, ModalBody, ModalHeader } from 'baseui/modal';
import { LabelMedium } from 'baseui/typography';
import { memo } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { ASSET_TYPE_CONTAINER } from '../../../constants/asset';
import { useStyles } from '../../../hooks/useStyles';
import { isPublicPageState } from '../../../storage/app';
import { currentStateState } from '../../../storage/client';
import { isVehicleKind } from '../../../utils/assets';
import AssetData from '../../shared/AssetData';
import AssetHeader from '../../shared/AssetHeader';
import ContainerData from '../../shared/ContainerData';
import VehicleData from '../../shared/VehicleData';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  ident?: string;
};

function CurrentStateModal({ onClose, isOpen, ident }: Readonly<Props>) {
  const { theme, classes } = useStyles();
  const currentState = useRecoilValue(currentStateState(ident ?? ''));
  const { formatDateFromTs } = useMoment();
  const isPublicPage = useRecoilValue(isPublicPageState);

  if (!currentState || !ident) return null;

  const { asset, battery_percentage, positionType } = currentState;
  const { container, vehicle } = asset;

  return createPortal(
    <Modal
      animate
      closeable
      onClose={onClose}
      isOpen={isOpen}
      size="auto"
    >
      <ModalHeader>
        <AssetHeader
          asset={asset}
          batteryPercentage={battery_percentage}
          positionType={positionType}
        />
      </ModalHeader>
      <ModalBody>
        <FlexColumn gap={theme.sizing.scale800}>
          <AssetData
            asset={asset}
            ident={currentState.ident}
            enabledLabelEdit={!isPublicPage}
          />
          {asset.assetType === ASSET_TYPE_CONTAINER && <ContainerData container={container} />}
          {isVehicleKind(asset.assetType) && (
            <VehicleData
              vehicle={vehicle}
              odometer={currentState.gnss_vehicle_mileage}
              hourometer={currentState.engine_motorhours}
            />
          )}
          <FlexColumn gap={theme.sizing.scale200}>
            <LabelMedium className={classes.dataHeaderClass}>Último reporte</LabelMedium>
            <FlexColumn
              gap={theme.sizing.scale200}
              classNames={classes.dataContentClass}
            >
              <LabeledData
                label="Fecha:"
                content={formatDateFromTs(+currentState.timestamp)}
              />
              <LabeledData
                label="Latitud:"
                content={currentState.position_latitude?.toString()}
              />
              <LabeledData
                label="Longitud:"
                content={currentState.position_longitude?.toString()}
              />
              <LabeledData
                label="Dirección:"
                content={
                  <StyledLink
                    href={`https://maps.google.com/?q=${currentState.position_latitude},${currentState.position_longitude}`}
                    target="_blank"
                    style={{
                      color: theme.colors.contentSecondary,
                      ...theme.typography.ParagraphSmall,
                      maxWidth: '200px',
                    }}
                  >
                    <EllipsedText maxLines={2}>{currentState.geoReference}</EllipsedText>
                  </StyledLink>
                }
              />
              <LabeledData
                label="Punto de entrega:"
                content={asset.deliveryFence?.name || '-'}
              />
              <LabeledData
                label="Cerco:"
                content={asset.geoFence?.name || '-'}
              />
            </FlexColumn>
          </FlexColumn>
        </FlexColumn>
      </ModalBody>
    </Modal>,
    document.body,
  );
}

export default memo(CurrentStateModal);
